import PageTitle from '../../../components/atoms/PageTitle/PageTitle';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Row, Col, Alert, Card, Button, Nav, Tab, ProgressBar, Badge } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import { RankBadge } from '../../../components/atoms/User/RankBadge';
import { useUser } from '../../../hooks';
import useFriendship from '../friendship/hooks/useFriendship';
import useProfile from '../profile/hooks/useProfile';
import { AddFriendButton } from '../../../components/atoms/User/Friendship/AddFriendButton';
import Spinner from '../../../components/atoms/Spinner/Spinner';
import { convertToHumanDate } from '../../../utils';
import { Language, Languages } from '../../../layouts/types';
import Chart from 'react-apexcharts';
import ProfileFriendsList from '../../../components/molecules/User/ProfileFriendsList';
import { ReportButton } from '../../../components/atoms/Suport/Report/ReportButton';
import { ProblemSummary } from '../../../types/problem/problem';
import { CommentDetails } from '../../../types/cms/comment';
import { SeoEntityType } from '../../../types/cms/seoData';
import { SeoDetails } from '../../../components/atoms/CMS/SeoDetails';
import { DeleteFriendshipButton } from 'components/atoms/User/Friendship/DeleteFriendshipButton';
import { AcceptFriendButton } from 'components/atoms/User/Friendship/AcceptFriendButton';

type TabContentItem = {
    id: number;
    title: string;
    link: string;
};

const UserProfile = () => {
    const { t, i18n } = useTranslation();
    const { loadCheckFriendship, friendshipRelation, userDestroyFriendship, friendshipStatusChanged } = useFriendship();
    const { userProfileData, loadProfileUserData } = useProfile();
    const { userInfo, userLoadInfo, isUserAuthenticated } = useUser();
    const { username } = useParams<{ username: string }>();
    const [selectedTab, setSelectedTab] = useState<number>(1);
    const [canSend, setCanSend] = useState<boolean>(true)
    const [currentLang, setCurrentLang] = useState<Language | null>(null);
    const [profileNotFound, setProfileNotFound] = useState<boolean>(false);

    useEffect(() => {
        const currentLang = Languages.find((lang) => lang.code === i18n.language) ?? Languages[0];
        setCurrentLang(currentLang);
    }, [i18n.language]);


    const tabContents: TabContentItem[] = [
        {
            id: 1,
            title: t('profile.details_tab'),
            link: '#',
        },
        {
            id: 2,
            title: t('profile.comments_tab'),
            link: '#',
        },
        {
            id: 3,
            title: t('profile.card_statistics_title'),
            link: '#',
        },
        {
            id: 4,
            title: t('profile.notifications_tab'),
            link: '/notifications',
        },
    ];

    const isProfileVisitor = (): boolean => {
        if (!isUserAuthenticated) return true;
        return userInfo?.id !== userProfileData.user.id;
    };

    useEffect(() => {
        loadProfileUserData(username!!);
        if (isUserAuthenticated)
            loadCheckFriendship(username!!);
    }, [username]);

    useEffect(() => {
        if (!userInfo && isUserAuthenticated) userLoadInfo();
    }, [userInfo]);

    useEffect(() => {
        setTimeout(() => {
            setProfileNotFound(true);
        }, 5000);
    }, []);

    const [apexOpts, setApexOpts] = useState<any>(null);
    const [apexData, setApexData] = useState<number[]>([]);

    useEffect(() => {
        if (userProfileData) {
            setApexData([Math.floor((userProfileData.chartDetails.totalSolved * 100) / userProfileData.chartDetails.total)]);
            setApexOpts({
                grid: {
                    padding: {
                        left: 0,
                        right: 0,
                    },
                },
                chart: {
                    type: 'radialBar',
                    parentHeightOffset: 0,
                    toolbar: {
                        show: false,
                    },
                },
                plotOptions: {
                    radialBar: {
                        dataLabels: {
                            name: {
                                fontSize: '16px',
                            },
                            value: {
                                fontSize: '13px',
                            },
                        },
                    },
                },
                colors: ['#0acf97'],
                labels: [t('profile.chart_label')],
            });
        }
    }, [userProfileData]);

    return (
        <>
            <SeoDetails type={SeoEntityType.USER} slug={username ?? ''} />
            <PageTitle
                breadCrumbItems={[
                    {
                        label: t('profile.title'),
                        path: '/',
                        active: true,
                    },
                ]}
                title={t('profile.title')}
            />
            <Row className={'justify-content-center'}>
                {userProfileData && (
                    <>
                        <Col lg={12}>
                            <Card className='mb-3'>
                                <Card.Title className={'card-header bg-primary text-white text-uppercase my-0'}>{t('profile.card_general_title')}</Card.Title>
                                <Card.Body className='p-0 profile-card-border'>
                                    <div className={'row p-2'}>
                                        <div className={'col-sm-12'}>
                                            <div className={'align-items-center row'}>
                                                <div className={'col-auto col'}>
                                                    <div className={'avatar-xl'}>
                                                        <img src={userProfileData.user.avatarUrl} alt='' className={'rounded-circle img-thumbnail'} style={{ height: '100px' }} />
                                                    </div>
                                                </div>
                                                <div className={'col'}>
                                                    <div>
                                                        <h3 className={'mt-1 mb-1'}>
                                                            <span>
                                                                {userProfileData.user.displayName}
                                                                {isProfileVisitor() && isUserAuthenticated && (
                                                                    <span className={'float-end'}>
                                                                        <ReportButton entityType={'UserReport'} entityId={userProfileData.user.id} entityCommentType={null}></ReportButton>
                                                                    </span>
                                                                )}
                                                            </span>
                                                            {userProfileData.user.roles.map((role: string, index: number) => {
                                                                if (role == 'USER') return null;
                                                                return (
                                                                    <span className={'ms-1'} key={`role-${index}`}>
                                                                        <RankBadge role={role.toLowerCase()} pill={true}></RankBadge>
                                                                    </span>
                                                                );
                                                            })}
                                                        </h3>
                                                        {userProfileData.description && <div dangerouslySetInnerHTML={{ __html: userProfileData.description }} />}
                                                        {!userProfileData.description && <p className={'font-15 mb-0'}>{t('profile.no_description')}</p>}
                                                        <p className={'font-15'}>{userProfileData.county == 'no_county' ? t('profile.no_county') : userProfileData.county}</p>
                                                        {currentLang && (
                                                            <p className={'text-muted'}>
                                                                {t('profile.registered_message')} {convertToHumanDate(userProfileData.createdAt, currentLang.code)}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <hr className='mb-1 mt-0' />
                                    <Row>
                                        <Col lg={12} className={'d-flex justify-content-between align-items-center overflow-x-auto'}>
                                            <Tab.Container>
                                                <Nav variant='tabs' className='profile-nav nav-bordered border-0' as='ul'>
                                                    {tabContents.map((item: TabContentItem) => {
                                                        if (item.id === 4 && isProfileVisitor()) return null;
                                                        return (
                                                            <Nav.Item key={item.id.toString()} as={'li'}>
                                                                <Nav.Link
                                                                    active={selectedTab === item.id}
                                                                    as={Link}
                                                                    to={item.link}
                                                                    onClick={() => {
                                                                        setSelectedTab(item.id);
                                                                    }}
                                                                    target={item.id === 4 ? '_blank' : ''}
                                                                    eventKey={item.id}>
                                                                    <span className={'text-capitalize'}>{item.title}</span>
                                                                </Nav.Link>
                                                            </Nav.Item>
                                                        );
                                                    })}
                                                </Nav>
                                            </Tab.Container>
                                            <div className={'float-end ms-2 me-2'}>
                                                {isProfileVisitor() && isUserAuthenticated && friendshipRelation && (
                                                    <>
                                                        {friendshipRelation.id === null && (
                                                            <AddFriendButton 
                                                                canSend={canSend} 
                                                                setCanSend={setCanSend} 
                                                                friendshipRelation={friendshipRelation} 
                                                                paramUsername={username!!}>
                                                            </AddFriendButton>
                                                        )}
                                                        {friendshipRelation.id !== null && friendshipRelation.status == 0 && friendshipRelation.senderId !== userInfo.id && (
                                                            <AcceptFriendButton 
                                                                canSend={canSend} 
                                                                setCanSend={setCanSend} 
                                                                friendshipRelation={friendshipRelation} 
                                                                paramUsername={username!!}>
                                                            </AcceptFriendButton>
                                                        )}
                                                        {friendshipRelation.id !== null && friendshipRelation.status === 1 && (
                                                            <DeleteFriendshipButton 
                                                                canSend={canSend} 
                                                                setCanSend={setCanSend} 
                                                                friendshipId={friendshipRelation.id} 
                                                                friendshipRelationship={friendshipRelation} 
                                                                paramUsername={username!!}
                                                                buttonType={"delete"}
                                                            />
                                                        )}
                                                        {friendshipRelation.id !== null && friendshipRelation.status === 0 && (
                                                            <DeleteFriendshipButton 
                                                                canSend={canSend} 
                                                                setCanSend={setCanSend}
                                                                friendshipId={friendshipRelation.id} 
                                                                friendshipRelationship={friendshipRelation} 
                                                                paramUsername={username!!}
                                                                buttonType={friendshipRelation.senderId !== userInfo.id? "reject": "cancel"}
                                                            />
                                                        )}
                                                    </>
                                                )}
                                                {!isProfileVisitor() && (
                                                    <>
                                                        <Button as={Link as any} to={'/settings'} variant='dark' size='sm' className={'rounded-pill me-2'}>
                                                            {t('profile.settings_button_text')}
                                                        </Button>
                                                        <Button as={Link as any} to={'/friend-requests'} variant='info' size='sm' className={'rounded-pill me-2'}>
                                                            {t('profile.friend_requests_button_text')}
                                                        </Button>
                                                    </>
                                                )}
                                            </div>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </>
                )}
                {!userProfileData && (
                    <Col lg={8} className={'text-center'}>
                        <Alert variant={'info'} className={'mb-0'}>
                            {!profileNotFound && (
                                <>
                                    <p className={'mb-0'}>{t('profile.loading_message')}</p>
                                    <Spinner size='xs' className='mb-sm-1' color={'primary'} />
                                </>
                            )}
                            {profileNotFound && (
                                <p className={'mb-0'}>
                                    <i className={'mdi mdi-account-alert me-2 font-16'}></i>
                                    {t('profile.not_found_message')}
                                </p>
                            )}
                        </Alert>
                    </Col>
                )}
            </Row>
            {userProfileData && selectedTab === 1 && (
                <>
                    <Row>
                        <Col lg={6}>
                            <ProfileFriendsList username={userProfileData.user.username} />
                        </Col>
                        <Col lg={6}>
                            <Card>
                                <Card.Title className={'card-header bg-primary text-white text-uppercase my-0'}>
                                    {t('profile.card_statistics_title')} ({userProfileData.chartDetails.totalSolved}/{userProfileData.chartDetails.total})
                                </Card.Title>
                                <Card.Body className={'p-0'}>
                                    <Row>
                                        <Col lg={5}>{apexData && apexOpts && <Chart options={apexOpts} series={apexData} type='radialBar' height={200} className='apex-charts mt-0' />}</Col>
                                        <Col lg={7} style={{ padding: '1.5rem' }}>
                                            <span>
                                                {t('profile.chart_easy_problems_label')}
                                                <span className={'float-end'}>
                                                    <span className={'font-18 me-1'}>{userProfileData.chartDetails.easySolved}</span>
                                                    <span className={'text-muted font-15'}>/{userProfileData.chartDetails.easyTotal}</span>
                                                </span>
                                            </span>
                                            <ProgressBar
                                                now={Math.floor((userProfileData.chartDetails.easySolved * 100) / userProfileData.chartDetails.easyTotal)}
                                                className={'mt-1 mb-1'}
                                                label={`${Math.floor((userProfileData.chartDetails.easySolved * 100) / userProfileData.chartDetails.easyTotal)}%`}
                                                variant='success'
                                            />
                                            <span>
                                                {t('profile.chart_medium_problems_label')}
                                                <span className={'float-end'}>
                                                    <span className={'font-18 me-1'}>{userProfileData.chartDetails.mediumSolved}</span>
                                                    <span className={'text-muted font-15'}>/{userProfileData.chartDetails.mediumTotal}</span>
                                                </span>
                                            </span>
                                            <ProgressBar
                                                now={Math.floor((userProfileData.chartDetails.mediumSolved * 100) / userProfileData.chartDetails.mediumTotal)}
                                                className={'mt-1 mb-1'}
                                                label={`${Math.floor((userProfileData.chartDetails.mediumSolved * 100) / userProfileData.chartDetails.mediumTotal)}%`}
                                                variant='warning'
                                            />
                                            <span>
                                                {t('profile.chart_hard_problems_label')}
                                                <span className={'float-end'}>
                                                    <span className={'font-18 me-1'}>{userProfileData.chartDetails.hardSolved}</span>
                                                    <span className={'text-muted font-15'}>/{userProfileData.chartDetails.hardTotal}</span>
                                                </span>
                                            </span>
                                            <ProgressBar
                                                now={Math.floor((userProfileData.chartDetails.hardSolved * 100) / userProfileData.chartDetails.hardTotal)}
                                                className={'mt-1 mb-1'}
                                                label={`${Math.floor((userProfileData.chartDetails.hardSolved * 100) / userProfileData.chartDetails.hardTotal)}%`}
                                                variant='danger'
                                            />
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </>
            )}
            {userProfileData && selectedTab === 2 && (
                <Row>
                    <Col lg={12}>
                        <Card>
                            <Card.Title className={'card-header bg-primary text-white text-uppercase my-0'}>{t('comment.plural')}</Card.Title>
                            <Card.Body>
                                {userProfileData.allComments.map((comment: CommentDetails, index: number) => {
                                    return (
                                        <div className={'d-flex border shadow p-2 mt-2'} key={`profile-${index.toString()}`}>
                                            <img className='rounded-circle avatar-sm me-2' src={comment.user.avatarUrl} alt='' />
                                            <div className='w-100'>
                                                <h5 className='mt-0 mb-1'>
                                                    {isProfileVisitor() && (
                                                        <span className={'float-end'}>
                                                            <ReportButton entityType={'CommentReport'} entityId={comment.id} entityCommentType={comment.domain}></ReportButton>
                                                        </span>
                                                    )}
                                                    <Link to='#' className='fw-bolder'>
                                                        {comment.user.displayName}
                                                    </Link>
                                                    <br />
                                                    {currentLang && <small className='text-muted'>{convertToHumanDate(comment.updatedAt, currentLang!!.code)}</small>}
                                                </h5>
                                                <div className='mb-1'>
                                                    <span style={{ whiteSpace: 'break-spaces' }}>{comment.content}</span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}
            {userProfileData && selectedTab === 3 && (
                <Row>
                    <Col lg={12}>
                        <Card>
                            <Card.Title className={'card-header bg-primary text-white text-uppercase my-0'}>
                                {t('profile.card_statistics_title')} ({userProfileData.chartDetails.totalSolved}/{userProfileData.chartDetails.total})
                            </Card.Title>
                            <Card.Body>
                                {userProfileData.chartDetails.solvedProblems.map((problem: ProblemSummary, index: number) => {
                                    return (
                                        <Badge as={Link} target={'_blank'} to={`/problem/${problem.slug}`} bg={'info'} className={'m-1 font-14'} key={`badge-${index}`}>
                                            {problem.title}
                                        </Badge>
                                    );
                                })}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}
        </>
    );
};

export default UserProfile;
